import { useEffect, useMemo } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { Navigate, Route, Routes } from "react-router-dom";

import { RequireAuth } from "@/auth/RequireAuth";
import { AppShell } from "@/layout/AppShell";
import { ApiTokensAdminPage } from "@/pages/ApiTokensAdminPage";
import { ApplicationsAdminPage } from "@/pages/ApplicationsAdminPage";
import { ApplyPage } from "@/pages/ApplyPage";
import { AuditLogPage } from "@/pages/AuditLogPage";
import { BurnoutPage } from "@/pages/BurnoutPage";
import { CompliancePage } from "@/pages/CompliancePage";
import { AnnouncementsAdminPage } from "@/pages/AnnouncementsAdminPage";
import { ForecastPage } from "@/pages/ForecastPage";
import { WorkloadPage } from "@/pages/WorkloadPage";
import { RosterPage } from "@/pages/RosterPage";
import { HeatmapPage } from "@/pages/HeatmapPage";
import { HomePage } from "@/pages/HomePage";
import { OrgHealthPage } from "@/pages/OrgHealthPage";
import { SetupPasswordPage } from "@/pages/SetupPasswordPage";
import { SsoAdminPage } from "@/pages/SsoAdminPage";
import { SsoCallbackPage } from "@/pages/SsoCallbackPage";
import { SsoLinkCallbackPage } from "@/pages/SsoLinkCallbackPage";
import { ShiftPatternsAdminPage } from "@/pages/ShiftPatternsAdminPage";
import { FunctionalRolesAdminPage } from "@/pages/FunctionalRolesAdminPage";
import { SkillsAdminPage } from "@/pages/SkillsAdminPage";
import { SmtpSettingsPage } from "@/pages/SmtpSettingsPage";
import { TenantsAdminPage } from "@/pages/TenantsAdminPage";
import { TenantSettingsPage } from "@/pages/TenantSettingsPage";
import { TimesheetsAdminPage } from "@/pages/TimesheetsAdminPage";
import { UnavailabilitiesAdminPage } from "@/pages/UnavailabilitiesAdminPage";
import { UsersAdminPage } from "@/pages/UsersAdminPage";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { DesiderataRouter } from "@/pages/DesiderataRouter";
import { DirectoryPage } from "@/pages/DirectoryPage";
import { ExchangePage } from "@/pages/ExchangePage";
import { LogbookPage } from "@/pages/LogbookPage";
import { MyHomePage } from "@/pages/MyHomePage";
import { MyActivityPage } from "@/pages/MyActivityPage";
import { MyPlanningPage } from "@/pages/MyPlanningPage";
import { OrganizationsListPage } from "@/pages/OrganizationsListPage";
import { PlanningPage } from "@/pages/PlanningPage";
import { PractitionersListPage } from "@/pages/PractitionersListPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { SettingsPage } from "@/pages/SettingsPage";
import { useQuery } from "@tanstack/react-query";

import { getMyTenant } from "@/api/tenants";
import { useAuthStore } from "@/store/authStore";
import { useUIStore } from "@/store/uiStore";
import { buildTheme } from "@/theme";

export function App() {
  const { effectiveTheme, recomputeEffectiveTheme } = useUIStore();
  const accessToken = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => recomputeEffectiveTheme();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [recomputeEffectiveTheme]);

  const tenant = useQuery({
    queryKey: ["me-tenant"],
    queryFn: getMyTenant,
    enabled: Boolean(accessToken),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const brandColor = tenant.data?.brand_color ?? null;
  const theme = useMemo(
    () => buildTheme(effectiveTheme, brandColor),
    [effectiveTheme, brandColor],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/setup-password" element={<SetupPasswordPage />} />
        <Route path="/auth/sso/callback" element={<SsoCallbackPage />} />
        <Route path="/apply" element={<ApplyPage />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <AppShell>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/planning" element={<PlanningPage />} />
                  <Route path="/organizations" element={<OrganizationsListPage />} />
                  <Route path="/practitioners" element={<PractitionersListPage />} />
                  <Route path="/desiderata" element={<DesiderataRouter />} />
                  <Route path="/exchange" element={<ExchangePage />} />
                  <Route path="/logbook" element={<LogbookPage />} />
                  <Route path="/my-planning" element={<MyPlanningPage />} />
                  <Route path="/my-activity" element={<MyActivityPage />} />
                  <Route path="/my-home" element={<MyHomePage />} />
                  <Route path="/directory" element={<DirectoryPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/profile/sso/callback" element={<SsoLinkCallbackPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/audit" element={<AuditLogPage />} />
                  <Route path="/compliance" element={<CompliancePage />} />
                  <Route path="/burnout" element={<BurnoutPage />} />
                  <Route path="/forecast" element={<ForecastPage />} />
                  <Route path="/heatmap" element={<HeatmapPage />} />
                  <Route path="/org-health" element={<OrgHealthPage />} />
                  <Route path="/admin/users" element={<UsersAdminPage />} />
                  <Route path="/admin/applications" element={<ApplicationsAdminPage />} />
                  <Route path="/admin/tenants" element={<TenantsAdminPage />} />
                  <Route path="/admin/smtp" element={<SmtpSettingsPage />} />
                  <Route path="/admin/unavailabilities" element={<UnavailabilitiesAdminPage />} />
                  <Route path="/admin/skills" element={<SkillsAdminPage />} />
                  <Route path="/admin/functional-roles" element={<FunctionalRolesAdminPage />} />
                  <Route path="/admin/announcements" element={<AnnouncementsAdminPage />} />
                  <Route path="/workload" element={<WorkloadPage />} />
                  <Route path="/roster" element={<RosterPage />} />
                  <Route path="/admin/shift-patterns" element={<ShiftPatternsAdminPage />} />
                  <Route path="/admin/api-tokens" element={<ApiTokensAdminPage />} />
                  <Route path="/admin/sso" element={<SsoAdminPage />} />
                  <Route path="/admin/tenant-settings" element={<TenantSettingsPage />} />
                  <Route path="/admin/timesheets" element={<TimesheetsAdminPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AppShell>
            </RequireAuth>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}
